import { useCallback, useEffect, useState } from "react";
import { CheckCircle, XCircle, ArrowsClockwise, UserPlus, Storefront, ArrowSquareOut, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/**
 * OwnershipRequestsCard — admin queue for "Claim & Upgrade" submissions
 * created by non-owner users on the public profile pages.
 *
 * Each row shows requester ↔ truck ↔ current owner side-by-side so the
 * admin can make a snap judgment, then Approve (transfers `owner_id`
 * across `profile_drafts` + all `schedules`) or Reject (no DB writes
 * beyond the request row itself).
 *
 * Backend contract:
 *   GET  /api/admin/ownership-requests              → pending list
 *   POST /api/admin/ownership-requests/{id}/approve → transfer ownership
 *   POST /api/admin/ownership-requests/{id}/reject  → mark rejected
 */
export default function OwnershipRequestsCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ownership-requests");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't load requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const decide = async (id, action) => {
    setBusyId(id);
    try {
      await api.post(`/admin/ownership-requests/${id}/${action}`, {});
      toast.success(action === "approve" ? "Ownership transferred." : "Request rejected.");
      setRows((rs) => rs.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || `Couldn't ${action}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section
      className="rounded-2xl border border-border bg-card p-5 sm:p-6"
      data-testid="ownership-requests-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <UserPlus weight="duotone" className="h-5 w-5 text-primary" />
            <h2 className="font-display font-bold text-lg sm:text-xl">Ownership requests</h2>
            <span
              className="inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded-full bg-primary/15 text-primary text-[11px] font-bold"
              data-testid="ownership-requests-count"
            >
              {rows.length}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Truck owners or staff who clicked "Claim &amp; Upgrade" on a profile they don't own.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-border text-xs font-semibold hover:bg-muted transition-colors disabled:opacity-50"
          data-testid="ownership-requests-refresh"
          aria-label="Refresh"
        >
          <ArrowsClockwise className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground" data-testid="ownership-requests-loading">
          <Spinner className="h-5 w-5 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div
          className="text-center py-8 text-sm text-muted-foreground"
          data-testid="ownership-requests-empty"
        >
          No pending requests. Nice and quiet — for now.
        </div>
      ) : (
        <ul className="space-y-2.5">
          {rows.map((r) => (
            <RequestRow
              key={r.id}
              row={r}
              busy={busyId === r.id}
              onApprove={() => decide(r.id, "approve")}
              onReject={() => decide(r.id, "reject")}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function RequestRow({ row, busy, onApprove, onReject }) {
  return (
    <li
      className="rounded-xl border border-border bg-background p-3.5"
      data-testid={`ownership-request-${row.id}`}
    >
      <div className="flex flex-wrap items-start gap-3 gap-y-2.5">
        {/* Truck */}
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
            <Storefront weight="duotone" className="h-3 w-3" /> Truck
          </div>
          <a
            href={`/t/${row.truck_slug}`}
            target="_blank"
            rel="noreferrer"
            className="font-display font-bold text-sm hover:text-primary transition-colors inline-flex items-center gap-1"
            data-testid="ownership-request-truck-link"
          >
            {row.truck_name}
            <ArrowSquareOut className="h-3 w-3" />
          </a>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            /t/{row.truck_slug}
          </div>
        </div>

        {/* Requester */}
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
            <UserPlus weight="duotone" className="h-3 w-3" /> Requested by
          </div>
          <div className="text-sm font-semibold truncate" data-testid="ownership-request-email">
            {row.requested_by_email}
          </div>
          {row.requested_by_name && (
            <div className="text-[11px] text-muted-foreground truncate">{row.requested_by_name}</div>
          )}
        </div>

        {/* Current owner */}
        <div className="flex-1 min-w-[200px]">
          <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
            Currently owned by
          </div>
          <div className="text-sm truncate">
            {row.current_owner_email || <span className="text-muted-foreground">— no owner —</span>}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {new Date(row.created_at).toLocaleString([], {
              month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
            })}
          </div>
        </div>
      </div>

      {row.note && (
        <div className="mt-2.5 rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground italic">
          "{row.note}"
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2 justify-end">
        <button
          type="button"
          onClick={onReject}
          disabled={busy}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-border text-xs font-semibold hover:bg-muted transition-colors disabled:opacity-50"
          data-testid="ownership-request-reject-btn"
        >
          <XCircle weight="duotone" className="h-3.5 w-3.5" /> Reject
        </button>
        <button
          type="button"
          onClick={onApprove}
          disabled={busy}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
          data-testid="ownership-request-approve-btn"
        >
          <CheckCircle weight="fill" className="h-3.5 w-3.5" />
          {busy ? "Transferring…" : "Approve & transfer"}
        </button>
      </div>
    </li>
  );
}
